import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { getInterview, updateInterview } from "../../../_api/Talent";
import { getUsers } from "../../../_api/Admin";
import { Loader } from "../../../_helpers/components/Forms/Loader";
import { BackButton } from "../../../_helpers/components/BackButton";
import { Button } from "../../../_helpers/components/Button";

const TYPE_LABELS = { ANNUEL: "Annuel", PROFESSIONNEL: "Professionnel", EVAL_360: "Évaluation 360°", RECRUTEMENT: "Recrutement" };
const FORMAT_LABELS = { PRESENTIEL: "Présentiel", VISIO: "Visio" };
const SUBTYPE_LABELS = { RH: "RH", TECHNIQUE: "Technique", MANAGER: "Manager", FINAL: "Final" };
const DECISION_LABELS = {
    PENDING: "En attente",
    A_REVOIR: "À revoir",
    REFUSE: "Refusé",
    SHORTLIST: "Shortlist",
    VALIDE: "Validé pour étape suivante",
};

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "");
const formatDateInput = (d) => (d ? new Date(d).toLocaleDateString("fr-CA").slice(0, 10) : "");

const SectionCard = ({ title, children }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">{title}</h2>
        {children}
    </div>
);

export const EntretienDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [interview, setInterview] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [form, setForm] = useState({});

    const isRecrutement = interview?.type === "RECRUTEMENT";
    const notes = interview?.notes || {};
    const formNotes = form.notes || notes;

    const fetch = () => {
        if (!id) return;
        setLoading(true);
        Promise.all([
            getInterview(id),
            getUsers().catch(() => ({ data: [] })),
        ])
            .then(([iRes, uRes]) => {
                const i = iRes.data ?? iRes;
                setInterview(i);
                setUsers(uRes.data || []);
                setForm({
                    date: formatDateInput(i.date),
                    time: i.time || "",
                    evaluator_id: i.evaluator_id || "",
                    format: i.format || "",
                    location_or_link: i.location_or_link || "",
                    interview_subtype: i.interview_subtype || "",
                    notes: {
                        points_forts: notes.points_forts || "",
                        points_vigilance: notes.points_vigilance || "",
                        reponses_marquantes: notes.reponses_marquantes || "",
                        comportement_attitude: notes.comportement_attitude || "",
                    },
                    decision_status: i.decision_status || "",
                    decision_comment: i.decision_comment || "",
                    salary_proposal: i.salary_proposal || "",
                    follow_up_date: formatDateInput(i.follow_up_date),
                    candidate_source: i.candidate_source || "",
                });
            })
            .catch((err) => setError(err?.response?.data?.message || "Erreur"))
            .finally(() => setLoading(false));
    };

    useEffect(() => fetch(), [id]);

    const updateField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
    const updateNote = (key, value) => setForm((f) => ({
        ...f, notes: { ...(f.notes || {}), [key]: value },
    }));

    const handleSave = (e) => {
        e.preventDefault();
        setSaving(true);
        const payload = {
            date: form.date || undefined,
            time: form.time || undefined,
            evaluator_id: form.evaluator_id || undefined,
            format: form.format || undefined,
            location_or_link: form.location_or_link || undefined,
            interview_subtype: isRecrutement ? (form.interview_subtype || undefined) : undefined,
            notes: Object.keys(formNotes).some((k) => formNotes[k]) ? formNotes : undefined,
            decision_status: form.decision_status || undefined,
            decision_comment: form.decision_comment || undefined,
            salary_proposal: isRecrutement ? (form.salary_proposal || undefined) : undefined,
            follow_up_date: form.follow_up_date || undefined,
            candidate_source: isRecrutement ? (form.candidate_source || undefined) : undefined,
        };
        updateInterview(id, payload)
            .then(() => {
                toast.success("Entretien mis à jour.");
                fetch();
            })
            .catch((err) => toast.error(err?.response?.data?.message || "Erreur"))
            .finally(() => setSaving(false));
    };

    const person = interview?.user || interview?.candidate;
    const personName = person ? `${person.first_name || ""} ${person.last_name || ""}`.trim() : "-";
    const backUrl = interview?.candidate_id
        ? `/talents/recrutement/${interview.candidate_id}`
        : interview?.user_id
            ? `/talents/collaborateurs/${interview.user_id}`
            : "/talents/entretiens";

    if (loading) return <Loader text="Chargement..." />;
    if (error || !interview) {
        return (
            <div className="min-h-full bg-gray-50 flex items-center justify-center">
                <div className="text-center text-red-600">{error || "Entretien introuvable"}</div>
                <button onClick={() => navigate("/talents/entretiens")} className="mt-4 text-app-primary">Retour</button>
            </div>
        );
    }

    const inputClass = "w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-app-primary focus:border-app-primary";
    const labelClass = "block text-sm font-medium text-gray-700 mb-1";

    return (
        <div className="min-h-full bg-gray-50">
            <div className="bg-white border-b border-gray-200 shadow-sm">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
                    <div className="flex items-center gap-3">
                        <BackButton
                            onClick={() => navigate("/talents/entretiens")}
                            title="Retour"
                        />
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">
                                Entretien {TYPE_LABELS[interview.type] || interview.type} — {personName}
                            </h1>
                            <p className="text-sm text-gray-500">
                                {formatDate(interview.date)}
                                {interview.evaluator && ` · ${interview.evaluator.first_name} ${interview.evaluator.last_name}`}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSave} className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
                {/* Infos entretien */}
                <SectionCard title="🔹 Informations entretien">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className={labelClass}>Date</label>
                            <input type="date" value={form.date || ""} onChange={(e) => updateField("date", e.target.value)} className={inputClass} />
                        </div>
                        <div>
                            <label className={labelClass}>Heure</label>
                            <input type="time" value={form.time || ""} onChange={(e) => updateField("time", e.target.value)} className={inputClass} placeholder="14:00" />
                        </div>
                        <div>
                            <label className={labelClass}>Type</label>
                            <input type="text" value={TYPE_LABELS[interview.type] || interview.type} className={inputClass} disabled />
                        </div>
                        {isRecrutement && (
                            <div>
                                <label className={labelClass}>Sous-type</label>
                                <select value={form.interview_subtype || ""} onChange={(e) => updateField("interview_subtype", e.target.value)} className={inputClass}>
                                    <option value="">— Choisir —</option>
                                    {Object.entries(SUBTYPE_LABELS).map(([k, v]) => (
                                        <option key={k} value={k}>{v}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div>
                            <label className={labelClass}>Format</label>
                            <select value={form.format || ""} onChange={(e) => updateField("format", e.target.value)} className={inputClass}>
                                <option value="">— Choisir —</option>
                                {Object.entries(FORMAT_LABELS).map(([k, v]) => (
                                    <option key={k} value={k}>{v}</option>
                                ))}
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className={labelClass}>Lieu ou lien visio</label>
                            <input type="text" value={form.location_or_link || ""} onChange={(e) => updateField("location_or_link", e.target.value)} className={inputClass} placeholder="Adresse ou URL de la visioconférence" />
                        </div>
                        <div>
                            <label className={labelClass}>Intervieweur / Évaluateur</label>
                            <select value={form.evaluator_id || ""} onChange={(e) => updateField("evaluator_id", e.target.value)} className={inputClass}>
                                <option value="">— Choisir —</option>
                                {(users || []).filter((u) => u.activated !== false).map((u) => (
                                    <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </SectionCard>

                {/* Infos candidat (recrutement uniquement) */}
                {isRecrutement && (
                    <SectionCard title="🔹 Informations candidat">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className={labelClass}>Nom / Prénom</label>
                                <input
                                    type="text"
                                    value={interview.candidate ? `${interview.candidate.first_name} ${interview.candidate.last_name}` : ""}
                                    className={inputClass}
                                    disabled
                                />
                            </div>
                            <div>
                                <label className={labelClass}>Poste visé</label>
                                <input
                                    type="text"
                                    value={interview.candidate?.job_offer?.title || ""}
                                    className={inputClass}
                                    disabled
                                />
                            </div>
                            <div>
                                <label className={labelClass}>Département / Équipe</label>
                                <input
                                    type="text"
                                    value={interview.candidate?.establishment?.name || ""}
                                    className={inputClass}
                                    disabled
                                />
                            </div>
                            <div>
                                <label className={labelClass}>Référence offre</label>
                                <input type="text" value={interview.candidate?.job_offer_id ? `#${interview.candidate.job_offer_id}` : ""} className={inputClass} disabled />
                            </div>
                            <div className="md:col-span-2">
                                <label className={labelClass}>Source de candidature</label>
                                <input type="text" value={form.candidate_source || ""} onChange={(e) => updateField("candidate_source", e.target.value)} className={inputClass} placeholder="LinkedIn, cooptation, etc." />
                            </div>
                        </div>
                    </SectionCard>
                )}

                {/* Notes libres */}
                <SectionCard title="💬 Notes libres de l'intervieweur">
                    <div className="space-y-4">
                        <div>
                            <label className={labelClass}>Points forts</label>
                            <textarea value={formNotes.points_forts || ""} onChange={(e) => updateNote("points_forts", e.target.value)} className={inputClass} rows={2} />
                        </div>
                        <div>
                            <label className={labelClass}>Points de vigilance</label>
                            <textarea value={formNotes.points_vigilance || ""} onChange={(e) => updateNote("points_vigilance", e.target.value)} className={inputClass} rows={2} />
                        </div>
                        <div>
                            <label className={labelClass}>Réponses marquantes</label>
                            <textarea value={formNotes.reponses_marquantes || ""} onChange={(e) => updateNote("reponses_marquantes", e.target.value)} className={inputClass} rows={2} />
                        </div>
                        <div>
                            <label className={labelClass}>Comportement / attitude</label>
                            <textarea value={formNotes.comportement_attitude || ""} onChange={(e) => updateNote("comportement_attitude", e.target.value)} className={inputClass} rows={2} />
                        </div>
                    </div>
                </SectionCard>

                {/* Décision & suite */}
                <SectionCard title="🎯 Décision & suite du processus">
                    <div className="space-y-4">
                        <div>
                            <label className={labelClass}>Statut</label>
                            <select value={form.decision_status || ""} onChange={(e) => updateField("decision_status", e.target.value)} className={inputClass}>
                                <option value="">— Choisir —</option>
                                {Object.entries(DECISION_LABELS).map(([k, v]) => (
                                    <option key={k} value={k}>{v}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className={labelClass}>Commentaire décision</label>
                            <textarea value={form.decision_comment || ""} onChange={(e) => updateField("decision_comment", e.target.value)} className={inputClass} rows={3} />
                        </div>
                        {isRecrutement && (
                            <div>
                                <label className={labelClass}>Proposition salariale (si validé)</label>
                                <input type="text" value={form.salary_proposal || ""} onChange={(e) => updateField("salary_proposal", e.target.value)} className={inputClass} placeholder="Ex: 45 000 € brut/an" />
                            </div>
                        )}
                        <div>
                            <label className={labelClass}>Date de relance prévue</label>
                            <input type="date" value={form.follow_up_date || ""} onChange={(e) => updateField("follow_up_date", e.target.value)} className={inputClass} />
                        </div>
                    </div>
                </SectionCard>

                <div className="flex gap-3">
                    <Button
                        name="Enregistrer"
                        buttonType="submit"
                        loading={saving}
                    />
                    <button type="button" onClick={() => navigate(backUrl)} className="px-6 py-2 border border-gray-300 rounded-sm text-gray-700 font-medium hover:bg-gray-50">
                        Annuler
                    </button>
                </div>
            </form>
        </div>
    );
};
